import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable'

import { collisionDetection, getAccessibility, sortItems } from '../lib'

const useDefaultSensors = () => useSensors(
  useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10
    }
  }),
  useSensor(TouchSensor, {
    activationConstraint: {
      distance: 10
    }
  }),
  useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  })
)

/**
 * SortableContext creates the context for a list of sortable items
 *
 * @example
 * const SortableItem = ({ item, children }) => {
 *   const {
 *     sortable: {
 *       isDragging,
 *       attributes,
 *       listeners,
 *       setNodeRef
 *     },
 *     itemStyle
 *   } = useAdvancedSortable({
 *     id: item.id,
 *   })
 *
 *   return (
 *     <Sortable ref={setNodeRef} isDragging={isDragging} {...itemStyle}>
 *       <DragHandler {...attributes} {...listeners} />
 *       {JSON.stringify(item)}
 *     </Sortable>
 *   )
 * }
 *
 * const SortableList = () => {
 *   const [items, setItems] = useState([{id: 1}, {id: 2}, {id: 3}])
 *
 *   return (
 *     <SortableContext items={items} setItems={setItems}>
 *       {items.map((item) => (
 *         <SortableItem item={item} key={item.id} />
 *       ))}
 *     </SortableContext>
 *   )
 * }
 */
export const RectangularSortableContext = ({ items, setItems, customOnDragEndEvent, children }) => (
  <DndContext
    accessibility={getAccessibility(items)}
    sensors={useDefaultSensors()}
    collisionDetection={collisionDetection}
    onDragEnd={(event) => {
      if (customOnDragEndEvent && typeof customOnDragEndEvent === 'function') {
        customOnDragEndEvent(items, event)
      } else {
        const sorted = sortItems(items, event)

        if (sorted) {
          setItems(sorted)
        }
      }
    }}
    modifiers={[restrictToParentElement]}
  >
    <SortableContext
      items={items}
      strategy={rectSortingStrategy}
    >
      {children}
    </SortableContext>
  </DndContext>
)
